import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { motion } from 'framer-motion';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { SocialMedia } from '../../constants/links';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Organizations', href: '#organizations' },
  { label: 'Works', href: '#works' },
  { label: 'Skills', href: '#skills' },
];

const socialLinks = [
  { icon: <LinkedInIcon />, href: SocialMedia.LINKED_IN, label: 'LinkedIn' },
  { icon: <GitHubIcon />, href: SocialMedia.GITHUB, label: 'GitHub' },
  { icon: <EmailIcon />, href: SocialMedia.MAIL_TO, label: 'Email' },
];

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, transparent 0%, rgba(10, 10, 15, 0.8) 100%)',
        position: 'relative',
        borderTop: '1px solid rgba(68, 212, 130, 0.1)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left - CTA */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Fira Code, monospace',
                  fontSize: '0.85rem',
                  color: '#44D482',
                  letterSpacing: '0.1em',
                  mb: 2,
                }}
              >
                $ contact --init
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  fontSize: { xs: '2rem', md: '3rem' },
                  color: '#fff',
                  mb: 2,
                  lineHeight: 1.1,
                }}
              >
                LET'S MAKE SOMETHING
                <br />
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #44D482 0%, #6EEAA3 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  AMAZING TOGETHER
                </Box>
              </Typography>

              <Typography
                component="a"
                href={SocialMedia.MAIL_TO}
                sx={{
                  display: 'inline-block',
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontSize: '1.1rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  mt: 2,
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#44D482',
                  },
                }}
              >
                Start by saying{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#44D482',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                  }}
                >
                  hi →
                </Box>
              </Typography>
            </motion.div>
          </Grid>

          {/* Right - Links */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { md: 'flex-end' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: { xs: 4, md: 8 },
                }}
              >
                {/* Nav Links */}
                <Stack spacing={2}>
                  <Typography
                    sx={{
                      fontFamily: 'Fira Code, monospace',
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.4)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Navigation
                  </Typography>
                  {navLinks.map((link) => (
                    <Typography
                      key={link.label}
                      component="a"
                      href={link.href}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.95rem',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: '#44D482',
                        },
                      }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Stack>

                {/* Social Links */}
                <Stack spacing={2}>
                  <Typography
                    sx={{
                      fontFamily: 'Fira Code, monospace',
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.4)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Connect
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {socialLinks.map((social) => (
                      <IconButton
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            color: '#44D482',
                            background: 'rgba(68, 212, 130, 0.1)',
                          },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            mt: 8,
            pt: 4,
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {/* Brand */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Fira Code, monospace',
                fontSize: '1rem',
                color: '#44D482',
              }}
            >
              $ abhishek_
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: '1.5rem',
                color: '#fff',
              }}
            >
              KUMAR
            </Typography>
          </Box>

          {/* Copyright */}
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.4)',
            }}
          >
            © {new Date().getFullYear()} Abhishek Kumar. All rights reserved.
          </Typography>

          {/* Scroll to top */}
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
            <IconButton
              onClick={scrollToTop}
              sx={{
                background: 'rgba(68, 212, 130, 0.1)',
                border: '1px solid rgba(68, 212, 130, 0.2)',
                color: '#44D482',
                '&:hover': {
                  background: 'rgba(68, 212, 130, 0.2)',
                },
              }}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}
